import { FC, useEffect, useMemo, useState } from 'react'
import {
  lockedMedicalEventAlerts,
  MedicalEventNavBarProps,
  READONLY_MEDICAL_EVENT_ALERTS,
} from './MedicalEventNavBar.model'
import { NavBar } from '../../MainLayout'
import { getDisplayedName } from '../../../../misc/patient.utilities'
import { DocumentCategoryIndicator } from '../../../document'
import { DocumentCategoryKeys } from '../../../../model/DocumentCategory'
import { PatientAlertsBanner } from '../../../patient'
import { MedicalEventForm } from '../../../medicalEvent'
import { AlertBanner, ValidationModal } from '../../../shared'
import { RestuxLoadingState } from '../../../../store/restux/ui'
import { MedicalEventHeader } from './MedicalEventHeader'
import { useCurrentPatient, useGoBack } from '../../../../hooks/utils'
import { useLocation, useParams } from 'react-router-dom'
import { getNavigationOrigin } from '../../../../core/navigation/navigationOrigin'

export const MedicalEventNavBar: FC<MedicalEventNavBarProps> = ({
  medicalEvent,
  medicalEventLoadingState,
  isOwner,
  lockMedicalEvent,
}) => {
  const { currentPatient: patient } = useCurrentPatient()
  const { patientId } = useParams<'patientId'>()
  const { pathname } = useLocation()
  const [isEditing, setIsEditing] = useState(false)
  const [openLockModalValidation, setOpenLockModalValidation] = useState(false)
  const [backButtonTitle, setBackButtonTitle] = useState<string>()

  const displayedName = patient ? getDisplayedName(patient) : null
  // A medical event is opened from the timeline or from the transversal navigation: go back where we
  // came from, the timeline being the sensible destination on a direct access
  const goBack = useGoBack(`/patients/${patientId ?? ''}`)

  const handleLock = () => {
    if (medicalEvent) {
      lockMedicalEvent(medicalEvent)
    }
    setOpenLockModalValidation(false)
  }

  useEffect(() => {
    if (!backButtonTitle) {
      // the origin is recorded by the tracker, whose effect runs before this one
      if (getNavigationOrigin(pathname)?.includes('/browse_events')) {
        setBackButtonTitle('Retour vers la navigation transverse')
      } else {
        displayedName &&
          setBackButtonTitle(
            `Retour vers la page patient "${displayedName.firstname} ${displayedName.lastname}"`,
          )
      }
    }
  }, [backButtonTitle, displayedName, pathname])

  const testId = useMemo(
    () => (medicalEvent?.locked ? 'modal-medical_event-unlock' : 'modal-medical_event-lock'),
    [medicalEvent?.locked],
  )

  return (
    <>
      <NavBar
        noDivider={true}
        onBackButtonClick={goBack}
        backButtonTitle={backButtonTitle}
        stickyHeaderContent={
          <>
            <DocumentCategoryIndicator
              documentCategory={medicalEvent?.category ?? DocumentCategoryKeys.Other}
            />
            {!medicalEvent?.isEditable &&
              medicalEventLoadingState === RestuxLoadingState.IDLE &&
              (!medicalEvent?.locked ? (
                <AlertBanner alerts={READONLY_MEDICAL_EVENT_ALERTS} />
              ) : (
                <AlertBanner alerts={lockedMedicalEventAlerts(isOwner)} />
              ))}
            {isEditing && <MedicalEventForm />}
            <PatientAlertsBanner />
          </>
        }
      >
        <MedicalEventHeader
          editMedicalEvent={() => setIsEditing((value) => !value)}
          patient={patient ?? null}
          onLock={() => setOpenLockModalValidation(true)}
        />
      </NavBar>
      <ValidationModal
        display={openLockModalValidation}
        icon="lock"
        confirmLabel="Valider"
        title={
          medicalEvent?.locked
            ? 'Voulez vous vraiment déverrouiller cet évènement médical ? Ses documents ainsi que les valeurs de leurs variables seront éditables et mises à jour avec les nouvelles données du patient'
            : 'Voulez vous vraiment verrouiller cet évènement médical ? Ses documents ainsi que les valeurs de leurs variables seront figées'
        }
        onSubmit={handleLock}
        onClose={() => setOpenLockModalValidation(false)}
        testId={testId}
      />
    </>
  )
}
